import { FIELD_VALUE_TYPE } from '@constants'
import { FormRow, ObjectDataRecord, ObjectDataValue } from '@types'

/**
 * Определяет, нужно ли выполнять GET запрос после PUT для обогащения данных.
 *
 * Логика работы:
 * - Если responseData не передан: возвращает true, если были изменены поля типа
 *   OBJ_INTERNAL_ID_LINK или OBJ_PK_LINK (эти поля требуют обогащения).
 * - Если responseData передан: проверяет расхождения между отправленными данными
 *   и ответом сервера. Возвращает true, если обнаружены:
 *   - Лишние поля в ответе (после фильтрации вложенных полей)
 *   - Расхождения значений отправленных полей
 *
 * @param dirtyRowData - объект с измененными полями
 * @param fields - конфигурация полей формы
 * @param responseData - опциональный ответ с изменёнными данными от сервера
 * @returns true, если требуется GET запрос для обогащения данных
 */
export const shouldRefetchAfterUpdate = (
  dirtyRowData: ObjectDataRecord,
  fields: FormRow[],
  responseData?: ObjectDataRecord
): boolean => {
  const changedFieldCodes = Object.keys(dirtyRowData)

  // Проверяем поля, которые всегда требуют обогащения по типу
  const hasEnrichmentFields = changedFieldCodes.some(fieldCode => {
    const field = fields.find(f => f.code === fieldCode)
    if (!field) return false

    return (
      field.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ||
      field.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK
    )
  })

  // Если ответ от сервера не передан, возвращаем результат проверки по типам полей
  if (!responseData) {
    return hasEnrichmentFields
  }

  // Если есть ответ, проверяем расхождения данных между отправленными и полученными значениями
  return hasDataDiscrepancies(dirtyRowData, responseData)
}

/**
 * Проверяет наличие расхождений между отправленными данными и ответом сервера
 *
 * @param sentData - данные, отправленные в PUT запросе
 * @param responseData - данные, полученные в ответе от сервера
 * @returns true, если обнаружены расхождения (лишние поля или разные значения)
 */
const hasDataDiscrepancies = (
  sentData: ObjectDataRecord,
  responseData: ObjectDataRecord
): boolean => {
  const sentKeys = Object.keys(sentData)
  const responseKeys = Object.keys(responseData)

  // Фильтруем поля ответа: исключаем вложенные поля, у которых есть родительские в отправленных данных
  const filteredResponseKeys = filterExpandedFields(sentKeys, responseKeys)

  // Проверяем наличие лишних полей в ответе
  const hasExtraFields = filteredResponseKeys.some(responseKey => !sentKeys.includes(responseKey))

  // Возвращаем true, если есть лишние поля или расхождения значений
  return hasExtraFields || hasValueDiscrepancies(sentData, responseData, sentKeys)
}

/**
 * Фильтрует вложенные поля из ответа, оставляя только родительские ref поля
 * Вложенные поля (с точкой) исключаются, если есть соответствующие родительские поля
 *
 * @param sentKeys - ключи отправленных данных
 * @param responseKeys - ключи из ответа сервера
 * @returns отфильтрованный массив ключей ответа
 */
const filterExpandedFields = (sentKeys: string[], responseKeys: string[]): string[] => {
  return responseKeys.filter(responseKey => {
    // Если поле содержит точку - это вложенное поле
    // Исключаем вложенное поле, если есть соответствующее родительское в отправленных данных
    if (responseKey.includes('.')) {
      const rootField = responseKey.split('.')[0]

      return !sentKeys.includes(rootField)
    }

    // Родительские поля без точек всегда включаем
    return true
  })
}

/**
 * Проверяет расхождения значений между отправленными данными и ответом
 *
 * @param sentData - отправленные данные
 * @param responseData - данные ответа
 * @param sentKeys - ключи для проверки
 * @returns true, если найдены расхождения значений
 */
const hasValueDiscrepancies = (
  sentData: ObjectDataRecord,
  responseData: ObjectDataRecord,
  sentKeys: string[]
): boolean => {
  return sentKeys.some(key => {
    const sentValue = sentData[key]
    const responseValue = responseData[key]

    return !areValuesEqual(sentValue, responseValue)
  })
}

const areValuesEqual = (value1: ObjectDataValue, value2: ObjectDataValue): boolean => {
  // Оба значения null или undefined - считаем равными
  if (value1 == null && value2 == null) return true

  // Одно из значений null или undefined - считаем неравными
  if (value1 == null || value2 == null) return false

  if (typeof value1 === 'object' && typeof value2 === 'object') {
    return JSON.stringify(value1) === JSON.stringify(value2)
  }

  // Для примитивов сравниваем как строки (с приведением типов)
  return String(value1) === String(value2)
}
